/**
 * SimpleVerification Page
 *
 * Simplified 3-step verification workflow:
 * 1. Capture IXRS -> Verify fields (single screen)
 * 2. Capture Vial -> Verify & Match
 * 3. Review Result -> Email
 *
 * Uses the teaching system for protocol-specific AI learning.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button } from '../components/ui';
import { CameraCapture } from '../components/camera';
import { VerificationForm, type VerificationData } from '../components/verification';
import { extractAllIxrsFields, extractVialId, type ExtractionTemplateData } from '../lib/claudeVision';
import { compareNumbers } from '../lib/numberExtractor';
import { reconcileIds, getReconciliationStatus, type ReconciliationResult } from '../lib/reconcileIds';
import { useStudyTemplate, type TrainingExample } from '../hooks';
import { useIXVerifyAuth } from '../contexts';
import { supabase } from '../../services/supabase';
import { listSavedRecipients, invokeGenerateAndSend } from '../lib/ixverifyApi';
import { getCurrentSiteId } from '../../services/api';
import type { SavedRecipient } from '../types';

type VerificationStep =
  | 'select-mode'
  | 'capture-ixrs'
  | 'verify-ixrs'
  | 'capture-med'
  | 'verify-med'
  | 'result'
  | 'reconciliation'
  | 'email'
  | 'sending'
  | 'success';

interface VerificationState {
  // Images
  ixrsImage: string | null;
  medImages: string[]; // Support multiple med photos
  // IXRS Data
  ixrsData: VerificationData | null;
  // Med Data (extracted from med photos)
  medData: VerificationData | null;
  // Match Result
  matchResult: 'match' | 'mismatch' | null;
  matchSimilarity: number;
  // Notes
  notes: string;
  // Mode selection
  verificationMode: 'single' | 'multi';
  // Multi-mode reconciliation data
  ixrsDrugIds: string[];
  vialIds: string[];
  reconciliationResult: ReconciliationResult | null;
}

const STEPS = [
  { key: 'select-mode', label: 'Mode' },
  { key: 'capture-ixrs', label: 'IXRS Photo' },
  { key: 'verify-ixrs', label: 'Verify IXRS' },
  { key: 'capture-med', label: 'Med Photo' },
  { key: 'verify-med', label: 'Verify Med' },
  { key: 'result', label: 'Result' },
  { key: 'email', label: 'Report' },
];

function getDisplayStep(step: VerificationStep): string {
  if (step === 'sending' || step === 'success') return 'email';
  if (step === 'reconciliation') return 'result';
  return step;
}

// Success screen with auto-redirect countdown
interface SuccessScreenProps {
  verificationId?: string;
  onNewVerification: () => void;
  onReturnToDashboard: () => void;
}

function SuccessScreen({ verificationId, onNewVerification, onReturnToDashboard }: SuccessScreenProps) {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onReturnToDashboard();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onReturnToDashboard]);

  return (
    <div className="text-center py-12">
      <div className="text-6xl text-green-500 dark:text-green-400 mb-4">&#10003;</div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
        Report Sent Successfully!
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        The verification report has been emailed to your recipients.
      </p>

      {verificationId && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Verification ID: {verificationId}
        </p>
      )}

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Returning to dashboard in {countdown} seconds...
      </p>

      <div className="space-x-4">
        <Button onClick={onNewVerification}>
          Start New Verification
        </Button>
        <Button
          variant="outline"
          onClick={onReturnToDashboard}
        >
          Return to Dashboard Now
        </Button>
      </div>
    </div>
  );
}

export function SimpleVerification() {
  const navigate = useNavigate();
  const { user } = useIXVerifyAuth();
  const siteId = getCurrentSiteId();

  // Study template hook
  const {
    study,
    template,
    isTeachingMode,
    findOrCreateStudy,
    saveTemplate,
    recordFeedback
  } = useStudyTemplate();

  // Step state
  const [step, setStep] = useState<VerificationStep>('select-mode');

  // Verification data
  const [state, setState] = useState<VerificationState>({
    ixrsImage: null,
    medImages: [],
    ixrsData: null,
    medData: null,
    matchResult: null,
    matchSimilarity: 0,
    notes: '',
    verificationMode: 'single',
    ixrsDrugIds: [],
    vialIds: [],
    reconciliationResult: null
  });

  // Extraction state
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);

  // Email state
  const [emailRecipients, setEmailRecipients] = useState<string[]>(['']);
  const [savedRecipients, setSavedRecipients] = useState<SavedRecipient[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<{ verificationId: string; pdfUrl?: string } | null>(null);

  // Fetch saved recipients
  useEffect(() => {
    const fetchRecipients = async () => {
      if (!user?.site_id) return;
      try {
        const { data } = await listSavedRecipients(user.site_id);

        setSavedRecipients(data || []);
      } catch (error) {
        console.error('Error fetching recipients:', error);
      }
    };
    fetchRecipients();
  }, [user?.site_id]);

  // Trigger med extraction when entering verify-med step
  // In single mode, handleMedCapture already does extraction, so skip here
  useEffect(() => {
    if (step !== 'verify-med' || state.medImages.length === 0) return;
    if (isExtracting) return; // Already extracting
    // Skip if medData already has a vialId value (already extracted in handleMedCapture)
    if (state.medData?.vialId?.value) return;

    const extractMedIds = async () => {
      setIsExtracting(true);
      setExtractionError(null);

      try {
        const allExtractedIds: string[] = [];

        for (const img of state.medImages) {
          const result = await extractVialId(img);
          for (const id of result.ids) {
            if (id.value && !allExtractedIds.includes(id.value)) {
              allExtractedIds.push(id.value);
            }
          }
        }

        // In single mode, only use the first extracted ID
        const vialIdValue = state.verificationMode === 'single'
          ? (allExtractedIds[0] || '')
          : allExtractedIds.join(', ');

        const extractedMedData: VerificationData = {
          protocolId: state.ixrsData?.protocolId || { value: '', confidence: 'low' },
          subjectId: state.ixrsData?.subjectId || { value: '', confidence: 'low' },
          medId: state.ixrsData?.medId || { value: '', confidence: 'low' },
          vialId: {
            value: vialIdValue,
            confidence: allExtractedIds.length > 0 ? 'high' : 'low',
            context: state.verificationMode === 'single'
              ? 'Extracted from photo'
              : `Extracted from ${state.medImages.length} photo(s)`
          }
        };

        setState(prev => ({ ...prev, medData: extractedMedData }));
      } catch (error) {
        console.error('Med extraction error:', error);
        setExtractionError('Failed to extract Med ID. Please enter value manually.');
        setState(prev => ({
          ...prev,
          medData: {
            ...prev.ixrsData!,
            vialId: { value: '', confidence: 'low' }
          }
        }));
      } finally {
        setIsExtracting(false);
      }
    };

    extractMedIds();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, state.medImages.length]);

  // Progress calculation
  const displayStep = getDisplayStep(step);
  const currentStepIndex = STEPS.findIndex(s => s.key === displayStep);
  const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

  const goToStep = (newStep: VerificationStep) => {
    setStep(newStep);
    setExtractionError(null);
    setSendError(null);
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel this verification?')) {
      navigate('/ixverify/dashboard');
    }
  };

  // Mode selection handler
  const handleSelectMode = (mode: 'single' | 'multi') => {
    setState(prev => ({
      ...prev,
      verificationMode: mode,
      ixrsDrugIds: [],
      vialIds: [],
      reconciliationResult: null
    }));
    goToStep('capture-ixrs');
  };

  // Step 1: Handle IXRS capture
  const handleIxrsCapture = async (imageData: string) => {
    console.log('[IXVerify] Starting IXRS capture, image length:', imageData.length);
    setState(prev => ({ ...prev, ixrsImage: imageData }));
    setIsExtracting(true);
    setExtractionError(null);

    try {
      // Build template data for extraction if available
      const templateData: ExtractionTemplateData | undefined = template ? {
        field_hints: template.field_hints,
        training_examples: template.training_data?.map(ex => ({
          protocol_id: ex.protocol_id,
          subject_id: ex.subject_id,
          med_id: ex.med_id
        }))
      } : undefined;

      console.log('[IXVerify] Calling extractAllIxrsFields with template:', !!templateData);
      // Extract all IXRS fields in a single call (with template context if available)
      const result = await extractAllIxrsFields(imageData, templateData);
      console.log('[IXVerify] Extraction result:', JSON.stringify(result, null, 2));

      // Build extracted data from the result
      const extractedData: VerificationData = {
        protocolId: {
          value: result.protocolId?.value || '',
          confidence: result.protocolId?.confidence || 'low',
          context: result.protocolId?.context
        },
        subjectId: {
          value: result.subjectId?.value || '',
          confidence: result.subjectId?.confidence || 'low',
          context: result.subjectId?.context
        },
        medId: {
          value: result.medId?.value || '',
          confidence: result.medId?.confidence || 'low',
          context: result.medId?.context
        }
      };

      setState(prev => ({ ...prev, ixrsData: extractedData }));

      // Find or create study based on extracted protocol
      if (siteId && extractedData.protocolId.value) {
        await findOrCreateStudy(siteId, extractedData.protocolId.value);
      }

      goToStep('verify-ixrs');
    } catch (error) {
      console.error('IXRS extraction error:', error);
      setExtractionError('Failed to extract data from IXRS document. Please enter values manually.');
      // Still move to verify step so user can enter manually
      setState(prev => ({
        ...prev,
        ixrsData: {
          protocolId: { value: '', confidence: 'low' },
          subjectId: { value: '', confidence: 'low' },
          medId: { value: '', confidence: 'low' }
        }
      }));
      goToStep('verify-ixrs');
    } finally {
      setIsExtracting(false);
    }
  };

  // Step 2: Handle IXRS verification confirm
  const handleIxrsConfirm = async (data: VerificationData) => {
    setState(prev => ({ ...prev, ixrsData: data }));

    // Check if any field was edited (for teaching mode)
    const wasEdited = data.protocolId.wasEdited ||
      data.subjectId.wasEdited ||
      data.medId.wasEdited;

    console.log('[IXVerify] handleIxrsConfirm - wasEdited:', wasEdited);
    console.log('[IXVerify] handleIxrsConfirm - hook study:', study?.id, 'hook isTeachingMode:', isTeachingMode);

    // Always call findOrCreateStudy to get fresh teaching mode status
    // This ensures we have accurate state even if hook state is stale
    let currentStudy = study;
    let currentIsTeachingMode = isTeachingMode;

    if (siteId && data.protocolId.value) {
      const result = await findOrCreateStudy(siteId, data.protocolId.value);
      console.log('[IXVerify] handleIxrsConfirm - findOrCreateStudy result:', result);
      if (result) {
        currentStudy = result.study;
        currentIsTeachingMode = result.is_teaching_mode;
      }
    }

    console.log('[IXVerify] handleIxrsConfirm - currentStudy:', currentStudy?.id, 'currentIsTeachingMode:', currentIsTeachingMode);
    console.log('[IXVerify] handleIxrsConfirm - will save template:', !!(currentStudy && (currentIsTeachingMode || wasEdited)));

    // If in teaching mode or fields were edited, save to template
    if (currentStudy && (currentIsTeachingMode || wasEdited)) {
      const trainingExample: TrainingExample = {
        protocol_id: data.protocolId.value,
        subject_id: data.subjectId.value,
        med_id: data.medId.value,
        ai_extracted: state.ixrsData ? {
          protocol_id: state.ixrsData.protocolId.value,
          subject_id: state.ixrsData.subjectId.value,
          med_id: state.ixrsData.medId.value
        } : undefined,
        was_corrected: wasEdited || false,
        timestamp: new Date().toISOString()
      };

      console.log('[IXVerify] handleIxrsConfirm - calling saveTemplate with studyId:', currentStudy.id);
      // Save template with training example (don't send image - too large for server)
      const savedTemplate = await saveTemplate(currentStudy.id, {}, trainingExample);
      console.log('[IXVerify] handleIxrsConfirm - saveTemplate result:', savedTemplate);
    }

    goToStep('capture-med');
  };

  // Step 3: Handle med photo capture (used for single mode)
  const handleMedCapture = async (imageData: string) => {
    // In single mode, replace (not add to) the med images array
    const newMedImages = state.verificationMode === 'single'
      ? [imageData]
      : [...state.medImages, imageData];
    setState(prev => ({ ...prev, medImages: newMedImages }));
    setIsExtracting(true);
    setExtractionError(null);

    try {
      // Extract IDs from the photo
      const result = await extractVialId(imageData);
      const allExtractedIds = result.ids
        .map(id => id.value)
        .filter((v): v is string => !!v);

      // In single mode, only use the first extracted ID
      const vialIdValue = state.verificationMode === 'single'
        ? (allExtractedIds[0] || '')
        : allExtractedIds.join(', ');

      const extractedMedData: VerificationData = {
        protocolId: state.ixrsData?.protocolId || { value: '', confidence: 'low' },
        subjectId: state.ixrsData?.subjectId || { value: '', confidence: 'low' },
        medId: state.ixrsData?.medId || { value: '', confidence: 'low' },
        vialId: {
          value: vialIdValue,
          confidence: allExtractedIds.length > 0 ? 'high' : 'low',
          context: state.verificationMode === 'single'
            ? 'Extracted from photo'
            : `Extracted from ${newMedImages.length} photo(s)`
        }
      };

      setState(prev => ({ ...prev, medData: extractedMedData }));
      goToStep('verify-med');
    } catch (error) {
      console.error('Med extraction error:', error);
      setExtractionError('Failed to extract Med ID. Please enter value manually.');
      setState(prev => ({
        ...prev,
        medData: {
          ...prev.ixrsData!,
          vialId: { value: '', confidence: 'low' }
        }
      }));
      goToStep('verify-med');
    } finally {
      setIsExtracting(false);
    }
  };

  // Handle adding another med photo (go back to capture without losing existing photos)
  const handleAddAnotherMedPhoto = () => {
    goToStep('capture-med');
  };

  // Quick capture for med photos - just adds to array, no extraction until Done
  const handleMedCaptureQuick = (imageData: string) => {
    setState(prev => ({
      ...prev,
      medImages: [...prev.medImages, imageData]
    }));
    // autoRestart=true keeps camera ready for next photo
  };

  // Remove a med image by index
  const handleRemoveMedImage = (indexToRemove: number) => {
    setState(prev => ({
      ...prev,
      medImages: prev.medImages.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  // Remove the IXRS image
  const handleRemoveIxrsImage = () => {
    setState(prev => ({
      ...prev,
      ixrsImage: null,
      ixrsData: null
    }));
    goToStep('capture-ixrs');
  };

  // Step 4: Handle med verification confirm
  const handleMedConfirm = (data: VerificationData) => {
    if (state.verificationMode === 'single') {
      // Single mode: simple comparison
      const ixrsMedId = state.ixrsData?.medId.value || '';
      const photoMedId = data.vialId?.value || '';

      const comparison = compareNumbers(ixrsMedId, photoMedId);

      setState(prev => ({
        ...prev,
        medData: data,
        matchResult: comparison.match ? 'match' : 'mismatch',
        matchSimilarity: comparison.similarity
      }));

      // Record feedback for accuracy tracking
      if (study) {
        const wasSuccessful = comparison.match;
        const wasCorrected = data.vialId?.wasEdited || false;
        recordFeedback(study.id, wasSuccessful, wasCorrected);
      }

      goToStep('result');
    } else {
      // Multi-mode: parse IDs and run reconciliation
      const ixrsIds = (state.ixrsData?.medId.value || '').split(',').map(s => s.trim()).filter(Boolean);
      const vialIds = (data.vialId?.value || '').split(',').map(s => s.trim()).filter(Boolean);

      const result = reconcileIds(ixrsIds, vialIds);

      setState(prev => ({
        ...prev,
        medData: data,
        ixrsDrugIds: ixrsIds,
        vialIds: vialIds,
        reconciliationResult: result,
        matchResult: result.overallMatch ? 'match' : 'mismatch'
      }));

      // Record feedback for accuracy tracking
      if (study) {
        const wasSuccessful = result.overallMatch;
        const wasCorrected = data.vialId?.wasEdited || false;
        recordFeedback(study.id, wasSuccessful, wasCorrected);
      }

      goToStep('reconciliation');
    }
  };

  // Handle email send
  const handleSendEmail = async () => {
    const validRecipients = emailRecipients.filter(e => e.trim());
    if (validRecipients.length === 0) {
      setSendError('Please add at least one recipient');
      return;
    }

    goToStep('sending');
    setSendError(null);

    try {
      // Get current session and check if valid
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      console.log('[IXVerify] Current session:', sessionData?.session ? 'exists' : 'none');

      if (sessionData?.session) {
        const expiresAt = sessionData.session.expires_at;
        const expiresDate = expiresAt ? new Date(expiresAt * 1000) : null;
        console.log('[IXVerify] Session expires at:', expiresDate?.toISOString(), '- now:', new Date().toISOString());
      }

      if (!sessionData?.session) {
        throw new Error('You are not logged in. Please refresh the page and log in again.');
      }

      // Force refresh the session to get a new JWT
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error('[IXVerify] Session refresh error:', refreshError);
        throw new Error('Session expired. Please refresh the page and log in again.');
      }

      if (!refreshData?.session) {
        console.error('[IXVerify] Session refresh returned no session');
        throw new Error('Failed to refresh session. Please log in again.');
      }

      const newExpiresAt = refreshData.session.expires_at;
      const newExpiresDate = newExpiresAt ? new Date(newExpiresAt * 1000) : null;
      console.log('[IXVerify] New session expires at:', newExpiresDate?.toISOString());

      // Collect which fields were manually edited
      const manualOverrides: string[] = [];
      if (state.ixrsData?.protocolId.wasEdited) manualOverrides.push('Protocol ID');
      if (state.ixrsData?.subjectId.wasEdited) manualOverrides.push('Subject ID');
      if (state.ixrsData?.medId.wasEdited) manualOverrides.push('IXRS Med ID');
      if (state.medData?.vialId?.wasEdited) manualOverrides.push('Photo Med ID');

      // Build verification data based on mode
      const baseVerificationData = {
        protocolId: state.ixrsData?.protocolId.value,
        subjectId: state.ixrsData?.subjectId.value,
        ixrsNumber: state.ixrsData?.medId.value,
        vialNumber: state.medData?.vialId?.value,
        matchResult: state.matchResult,
        notes: state.notes,
        ixrsImage: state.ixrsImage,
        vialImage: state.medImages[0] || null,
        manualOverrides: manualOverrides.length > 0 ? manualOverrides : null
      };

      // Add multi-mode specific data
      const verificationData = state.verificationMode === 'multi'
        ? {
            ...baseVerificationData,
            isMultiVial: true,
            ixrsDrugIds: state.ixrsDrugIds,
            vialIds: state.vialIds,
            matchedPairs: state.reconciliationResult?.matchedPairs,
            unmatchedIxrsIds: state.reconciliationResult?.unmatchedIxrsIds,
            extraVialIds: state.reconciliationResult?.extraVialIds,
          }
        : {
            ...baseVerificationData,
            matchSimilarity: state.matchSimilarity,
          };

      // Call the generate-and-send edge function
      const requestBody = {
        type: state.verificationMode === 'multi' ? 'multi' : 'single',
        verificationData,
        recipients: validRecipients,
        siteId,
        userId: user?.id
      };
      console.log('[IXVerify] Calling generate-and-send with:', {
        ...requestBody,
        verificationData: {
          ...requestBody.verificationData,
          ixrsImage: requestBody.verificationData.ixrsImage ? '[IMAGE DATA]' : null,
          vialImage: requestBody.verificationData.vialImage ? '[IMAGE DATA]' : null,
        }
      });

      // JWT + site headers are attached automatically by the api client
      // (services/api.ts request interceptor, with 401 refresh-and-retry).
      const { data, error } = await invokeGenerateAndSend(requestBody);

      console.log('[IXVerify] Edge function response:', { data, error });

      if (error) {
        console.error('[IXVerify] generate-and-send error:', error);
        // The api helper already surfaces the server's error body via error.message
        // (it reads response.data.error), so no separate response-body parse needed.
        throw new Error(error.message || 'Report generation failed');
      }

      // Check if response indicates failure
      if (data && !data.success && data.error) {
        console.error('[IXVerify] Response indicates failure:', data.error);
        throw new Error(data.error);
      }

      setSendResult(data);
      goToStep('success');
    } catch (error: any) {
      console.error('Send error:', error);
      const errorMessage = error?.message || 'Failed to send verification report. Please try again.';
      setSendError(errorMessage);
      goToStep('email');
    }
  };

  // Navigation helpers
  const goBack = () => {
    switch (step) {
      case 'capture-ixrs':
        goToStep('select-mode');
        break;
      case 'verify-ixrs':
        goToStep('capture-ixrs');
        break;
      case 'capture-med':
        goToStep('verify-ixrs');
        break;
      case 'verify-med':
        goToStep('capture-med');
        break;
      case 'result':
        goToStep('verify-med');
        break;
      case 'reconciliation':
        goToStep('verify-med');
        break;
      case 'email':
        goToStep(state.verificationMode === 'multi' ? 'reconciliation' : 'result');
        break;
    }
  };

  // Render step content
  const renderStep = () => {
    switch (step) {
      case 'select-mode':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Select Verification Type</h2>
              <p className="text-gray-600 dark:text-gray-400">How many vials need to be verified?</p>
            </div>

            <div className="grid gap-4">
              {/* Single Vial Option */}
              <button
                onClick={() => handleSelectMode('single')}
                className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 text-left hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/40 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 dark:group-hover:bg-primary-800/60 transition-colors">
                    <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Single Med ID</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      Verify one drug vial against the IXRS document. Best for standard single-dose dispensing.
                    </p>
                  </div>
                  <svg className="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>

              {/* Multi-Vial Option */}
              <button
                onClick={() => handleSelectMode('multi')}
                className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 text-left hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/40 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
                    <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v4m-2-2h4" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Multiple Med IDs</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      Verify multiple drug vials against the IXRS document. Use when dispensing multiple doses at once.
                    </p>
                  </div>
                  <svg className="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-amber-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>

            <div className="text-center pt-4">
              <button
                onClick={handleCancel}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        );

      case 'capture-ixrs':
        return (
          <CameraCapture
            onCapture={handleIxrsCapture}
            onCancel={handleCancel}
            onBack={goBack}
            title="Capture IXRS Document"
            instructions="Position the IXRS confirmation page in the frame"
            aspectRatio="document"
            isProcessing={isExtracting}
          />
        );

      case 'verify-ixrs':
        return (
          <VerificationForm
            imageUrl={state.ixrsImage || undefined}
            extractedData={state.ixrsData || {}}
            isTeachingMode={isTeachingMode}
            isExtracting={isExtracting}
            singleMode={state.verificationMode === 'single'}
            onConfirm={handleIxrsConfirm}
            onBack={goBack}
            error={extractionError || undefined}
          />
        );

      case 'capture-med':
        // Single mode: Capture one photo and proceed
        if (state.verificationMode === 'single') {
          return (
            <CameraCapture
              onCapture={handleMedCapture}
              onCancel={handleCancel}
              onBack={goBack}
              title="Capture Medication Label"
              instructions="Position the medication label in the frame"
              aspectRatio="document"
              isProcessing={isExtracting}
            />
          );
        }

        // Multi mode: Allow multiple photos
        return (
          <div className="space-y-4">
            {/* Show existing med photos if any */}
            {state.medImages.length > 0 && (
              <Card>
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {state.medImages.length} photo(s) captured
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {state.medImages.map((img, idx) => (
                      <div key={idx} className="relative flex-shrink-0">
                        <img
                          src={img}
                          alt={`Med ${idx + 1}`}
                          className="w-20 h-20 object-cover rounded border"
                        />
                        <button
                          onClick={() => handleRemoveMedImage(idx)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md hover:bg-red-600"
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => goToStep('verify-med')}
                      className="flex-1"
                    >
                      Done - Verify Med IDs
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            <CameraCapture
              onCapture={handleMedCaptureQuick}
              onCancel={handleCancel}
              onBack={goBack}
              title="Capture Medication Label"
              instructions={state.medImages.length > 0
                ? "Take more photos or tap 'Done' above"
                : "Position the medication label in the frame"}
              aspectRatio="document"
              autoRestart={true}
            />
          </div>
        );

      case 'verify-med':
        return (
          <div className="space-y-4">
            {/* Show captured med photo(s) - different UI for single vs multi mode */}
            {state.medImages.length > 0 && state.verificationMode === 'single' && (
              <Card>
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Captured medication photo</p>
                  <img
                    src={state.medImages[0]}
                    alt="Medication"
                    className="w-full max-h-48 object-contain rounded border bg-gray-50 dark:bg-gray-900/40"
                  />
                </CardContent>
              </Card>
            )}
            {state.medImages.length > 0 && state.verificationMode === 'multi' && (
              <Card>
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {state.medImages.length} photo(s) captured
                  </p>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {state.medImages.map((img, idx) => (
                      <div key={idx} className="relative flex-shrink-0">
                        <img
                          src={img}
                          alt={`Med ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded border"
                        />
                        <button
                          onClick={() => handleRemoveMedImage(idx)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md hover:bg-red-600"
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            <VerificationForm
              extractedData={state.medData || {}}
              showVialId
              singleMode={state.verificationMode === 'single'}
              isExtracting={isExtracting}
              onConfirm={handleMedConfirm}
              onBack={goBack}
              error={extractionError || undefined}
            />
            {/* Only show "Add Another" in multi mode */}
            {state.verificationMode === 'multi' && (
              <div className="flex justify-center">
                <Button variant="outline" onClick={handleAddAnotherMedPhoto}>
                  + Add Another Med Photo
                </Button>
              </div>
            )}
          </div>
        );

      case 'result':
        return (
          <div className="space-y-6">
            {/* Result Card */}
            <Card className={state.matchResult === 'match'
              ? 'border-2 border-green-500 dark:border-green-600'
              : 'border-2 border-red-500'
            }>
              <CardContent className="text-center py-8">
                <div className={`text-6xl mb-4 ${state.matchResult === 'match' ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                  {state.matchResult === 'match' ? '\u2713' : '\u2717'}
                </div>
                <h2 className={`text-2xl font-bold ${state.matchResult === 'match' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                  {state.matchResult === 'match' ? 'IDs Match' : 'IDs Do Not Match'}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  Similarity: {Math.round(state.matchSimilarity * 100)}%
                </p>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card>
              <CardContent>
                <h3 className="font-semibold mb-4 text-gray-900 dark:text-gray-100">Verification Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Protocol:</span>
                    <span className="ml-2 font-medium">{state.ixrsData?.protocolId.value}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Subject:</span>
                    <span className="ml-2 font-medium">{state.ixrsData?.subjectId.value}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">IXRS Med ID:</span>
                    <span className="ml-2 font-medium">{state.ixrsData?.medId.value}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Photo Med ID:</span>
                    <span className="ml-2 font-medium">{state.medData?.vialId?.value}</span>
                  </div>
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={state.notes}
                    onChange={(e) => setState(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    rows={3}
                    placeholder="Add any notes about this verification..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={goBack}>
                &#8592; Back
              </Button>
              <Button onClick={() => goToStep('email')}>
                Continue to Email &#8594;
              </Button>
            </div>
          </div>
        );

      case 'reconciliation':
        return state.reconciliationResult ? (
          <div className="space-y-6">
            <Card className={`border-2 ${
              state.reconciliationResult.overallMatch
                ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                : 'border-red-500 bg-red-50 dark:bg-red-900/20'
            }`}>
              <CardContent className="p-6 text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  state.reconciliationResult.overallMatch ? 'bg-green-100 dark:bg-green-900/40' : 'bg-red-100 dark:bg-red-900/40'
                }`}>
                  {state.reconciliationResult.overallMatch ? (
                    <svg className="w-12 h-12 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
                <h2 className={`text-2xl font-bold mb-2 ${
                  state.reconciliationResult.overallMatch ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
                }`}>
                  {state.reconciliationResult.overallMatch ? 'ALL VERIFIED' : 'VERIFICATION ISSUE'}
                </h2>
                <p className={`text-sm ${
                  state.reconciliationResult.overallMatch ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                }`}>
                  {getReconciliationStatus(state.reconciliationResult).summary}
                </p>
              </CardContent>
            </Card>

            {/* Matched Pairs */}
            {state.reconciliationResult.matchedPairs.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Verified ({state.reconciliationResult.matchedPairs.length})
                  </h3>
                  <div className="space-y-2">
                    {state.reconciliationResult.matchedPairs.map((pair, index) => (
                      <div key={index} className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                        <div>
                          <span className="font-mono text-sm">IXRS: {pair.ixrsId}</span>
                          <span className="mx-2 text-gray-400 dark:text-gray-500">=</span>
                          <span className="font-mono text-sm">Vial: {pair.vialId}</span>
                        </div>
                        <span className="text-green-600 dark:text-green-400 text-sm">{Math.round(pair.similarity * 100)}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Unmatched IXRS IDs (Missing Vials) */}
            {state.reconciliationResult.unmatchedIxrsIds.length > 0 && (
              <Card className="border-red-200 dark:border-red-800">
                <CardContent className="p-4">
                  <h3 className="font-medium text-red-800 dark:text-red-300 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Missing Vials ({state.reconciliationResult.unmatchedIxrsIds.length})
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-2">These IXRS drug IDs were not found on any vial:</p>
                  <div className="space-y-1">
                    {state.reconciliationResult.unmatchedIxrsIds.map((id, index) => (
                      <div key={index} className="font-mono text-sm bg-red-50 dark:bg-red-900/20 rounded px-3 py-2">
                        {id}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Extra Vial IDs */}
            {state.reconciliationResult.extraVialIds.length > 0 && (
              <Card className="border-orange-200 dark:border-orange-800">
                <CardContent className="p-4">
                  <h3 className="font-medium text-orange-800 dark:text-orange-300 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Extra Vials ({state.reconciliationResult.extraVialIds.length})
                  </h3>
                  <p className="text-sm text-orange-700 dark:text-orange-400 mb-2">These vial IDs were not found on the IXRS:</p>
                  <div className="space-y-1">
                    {state.reconciliationResult.extraVialIds.map((id, index) => (
                      <div key={index} className="font-mono text-sm bg-orange-50 dark:bg-orange-900/20 rounded px-3 py-2">
                        {id}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <Card>
              <CardContent className="p-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={state.notes}
                  onChange={(e) => setState(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any notes about this verification..."
                  className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  rows={3}
                />
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={goBack} className="flex-1">
                &#8592; Back
              </Button>
              <Button onClick={() => goToStep('email')} className="flex-1">
                Continue to Email &#8594;
              </Button>
            </div>
          </div>
        ) : null;

      case 'email':
      case 'sending':
        return (
          <div className="space-y-6">
            {/* Report Preview */}
            <Card>
              <CardContent>
                <h3 className="font-semibold mb-4 text-gray-900 dark:text-gray-100">Report Preview</h3>

                {/* Match Result Banner */}
                <div className={`p-4 rounded-lg mb-4 ${
                  state.matchResult === 'match'
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl ${state.matchResult === 'match' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {state.matchResult === 'match' ? '\u2713' : '\u2717'}
                    </span>
                    <div>
                      <p className={`font-semibold ${state.matchResult === 'match' ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                        {state.matchResult === 'match' ? 'IDs Match' : 'IDs Do Not Match'}
                      </p>
                      <p className={`text-sm ${state.matchResult === 'match' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        Similarity: {Math.round(state.matchSimilarity * 100)}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Verification Details */}
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div className="space-y-2">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Protocol:</span>
                      <span className="ml-2 font-medium">{state.ixrsData?.protocolId.value || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Subject:</span>
                      <span className="ml-2 font-medium">{state.ixrsData?.subjectId.value || '-'}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">IXRS Med ID:</span>
                      <span className="ml-2 font-medium">{state.ixrsData?.medId.value || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Photo Med ID:</span>
                      <span className="ml-2 font-medium">{state.medData?.vialId?.value || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Image Thumbnails */}
                <div className="flex gap-4">
                  {state.ixrsImage && (
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">IXRS Document</p>
                      <img
                        src={state.ixrsImage}
                        alt="IXRS"
                        className="w-full max-h-32 object-contain rounded border bg-gray-50 dark:bg-gray-900/40"
                      />
                    </div>
                  )}
                  {state.medImages.length > 0 && (
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Medication ({state.medImages.length} photo{state.medImages.length > 1 ? 's' : ''})</p>
                      <div className="flex gap-1">
                        {state.medImages.slice(0, 3).map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Med ${idx + 1}`}
                            className="max-h-32 object-contain rounded border bg-gray-50 dark:bg-gray-900/40"
                            style={{ maxWidth: state.medImages.length > 1 ? '50%' : '100%' }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {state.notes && (
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Notes</p>
                    <p className="text-sm">{state.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recipients */}
            <Card>
              <CardContent>
                <h3 className="font-semibold mb-4 text-gray-900 dark:text-gray-100">Send To</h3>

                {sendError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <span className="text-red-500 dark:text-red-400 text-lg">&#9888;</span>
                      <div>
                        <p className="font-medium text-red-800 dark:text-red-300">Failed to send report</p>
                        <p className="text-sm text-red-700 dark:text-red-400 mt-1">{sendError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick add buttons */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quick Add
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {/* Me button */}
                    <button
                      onClick={() => {
                        const myEmail = user?.email;
                        if (myEmail && !emailRecipients.includes(myEmail)) {
                          setEmailRecipients(prev =>
                            prev[0] === '' ? [myEmail] : [...prev, myEmail]
                          );
                        }
                      }}
                      disabled={!user?.email || emailRecipients.includes(user?.email || '')}
                      className={`px-3 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1 ${
                        emailRecipients.includes(user?.email || '')
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400'
                          : 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-800/60'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Me
                    </button>

                    {/* Saved recipients */}
                    {savedRecipients.map((recipient) => (
                      <button
                        key={recipient.id}
                        onClick={() => {
                          if (!emailRecipients.includes(recipient.email)) {
                            setEmailRecipients(prev =>
                              prev[0] === '' ? [recipient.email] : [...prev, recipient.email]
                            );
                          }
                        }}
                        className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                          emailRecipients.includes(recipient.email)
                            ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400'
                            : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {recipient.label || recipient.email}
                      </button>
                    ))}

                    {savedRecipients.length === 0 && (
                      <span className="text-sm text-gray-400 dark:text-gray-500 italic">No saved recipients</span>
                    )}
                  </div>
                </div>

                {/* Email inputs */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Recipients
                  </label>
                  {emailRecipients.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          const newRecipients = [...emailRecipients];
                          newRecipients[index] = e.target.value;
                          setEmailRecipients(newRecipients);
                        }}
                        placeholder="email@example.com"
                        className="flex-1 px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      />
                      {emailRecipients.length > 1 && (
                        <button
                          onClick={() => {
                            setEmailRecipients(prev => prev.filter((_, i) => i !== index));
                          }}
                          className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          &#10005;
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setEmailRecipients(prev => [...prev, ''])}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    + Add another recipient
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={goBack}>
                &#8592; Back
              </Button>
              <div className="space-x-2">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/ixverify/dashboard')}
                >
                  Skip & Finish
                </Button>
                <Button
                  onClick={handleSendEmail}
                  disabled={step === 'sending'}
                >
                  {step === 'sending' ? 'Sending...' : 'Send Report'}
                </Button>
              </div>
            </div>
          </div>
        );

      case 'success':
        return (
          <SuccessScreen
            verificationId={sendResult?.verificationId}
            onNewVerification={() => {
              // Reset state for new verification
              setState({
                ixrsImage: null,
                medImages: [],
                ixrsData: null,
                medData: null,
                matchResult: null,
                matchSimilarity: 0,
                notes: '',
                verificationMode: 'single',
                ixrsDrugIds: [],
                vialIds: [],
                reconciliationResult: null
              });
              setEmailRecipients(['']);
              setSendResult(null);
              goToStep('select-mode');
            }}
            onReturnToDashboard={() => navigate('/ixverify/dashboard')}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100 pb-8">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Drug Verification</h1>
            <button
              onClick={handleCancel}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>

          {/* Progress Bar */}
          {step !== 'success' && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                {STEPS.map((s, index) => (
                  <span
                    key={s.key}
                    className={index <= currentStepIndex ? 'text-primary-600 dark:text-primary-400 font-medium' : ''}
                  >
                    {s.label}
                  </span>
                ))}
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {renderStep()}
      </div>
    </div>
  );
}

export default SimpleVerification;
